import { getApiErrorContext } from './api.js';

/**
 * Maps known API error codes to plain-English messages we can surface
 * in toasts and inline form errors. Falls back to the server-provided
 * message (or a transport-aware generic) when the code isn't on the
 * list. Auth pages keep using `getAuthErrorContext` directly because
 * they need typed `unlockAt` details — this helper is the catch-all
 * for everything else.
 */
const CODE_MAP: Record<string, string> = {
  UNAUTHENTICATED: 'Your session has ended. Please sign in again.',
  FORBIDDEN: "You don't have permission to do that.",
  ACCOUNT_LOCKED:
    'Account temporarily locked after too many failed sign-ins. Try again in a few minutes.',
  /* NOT "that link is no longer valid". `INVALID_TOKEN` is thrown for every
     token this API holds — the access JWT, the REFRESH token, a reset link, an
     install token — and the two link cases have their own screens, which read
     `getAuthErrorContext` directly rather than coming through here (see the
     note above). So the case that actually reaches this map is a dead SESSION:
     the client retries any 401 once through `/auth/refresh`, and when that
     refresh is itself rejected the original code surfaces here.
     It was telling a reader whose session had timed out — on the profile, on a
     form, anywhere — that a link they never followed had expired, and to
     request a new one of something that does not exist. */
  INVALID_TOKEN: 'Your session has ended. Please sign in again.',
  EXPIRED_TOKEN: 'That link has expired.',
  VALIDATION_ERROR: 'Some fields need attention — please check the form.',
  DUPLICATE_ENTRY: 'That value is already in use. Please choose another.',
  NOT_FOUND: "We couldn't find what you were looking for.",
  RATE_LIMITED: 'Too many requests. Please wait a minute and try again.',
  // The device approval gate. The login page renders its own copy for these two
  // (a pending device is informational, not an error) — these are the fallback
  // for anywhere else a 403 from the gate could surface, e.g. a toast.
  DEVICE_PENDING_APPROVAL:
    'This device is waiting for administrator approval. Try again once it has been approved.',
  DEVICE_NOT_APPROVED:
    'This device is not allowed to sign in. Contact your administrator if you think that is wrong.',
  OUTSIDE_GEOFENCE:
    "You're not at the worksite. Move closer to the office, or ask your manager to punch you in.",
  IP_NOT_ALLOWED:
    "Punching from this network isn't allowed. Please use the office Wi-Fi.",
  MOCK_LOCATION:
    'Your phone is reporting a fake location. Disable any GPS-spoofing app and try again.',
  LOW_GPS_ACCURACY:
    'GPS signal is too weak right now. Step into an open area and try again.',
  INTERNAL_ERROR:
    'Something went wrong on our end. Please try again, or contact support if it keeps happening.',
};

interface ValidationFieldDetail {
  path: unknown;
  message: unknown;
}

function isValidationFieldDetail(value: unknown): value is ValidationFieldDetail {
  if (value === null || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return 'path' in record && 'message' in record;
}

/**
 * The message the API's zod middleware attaches to every schema failure. It
 * always travels WITH `details.fields`, so on its own it tells the user
 * nothing — that's the one case where the generic CODE_MAP copy reads better.
 */
const GENERIC_VALIDATION_MESSAGE = 'Request validation failed';

export function friendlyMessage(err: unknown): string {
  const ctx = getApiErrorContext(err);

  // Transport / offline detection — when there's no HTTP status and the
  // code didn't get parsed, assume the request didn't make it out.
  if (ctx.status === undefined && ctx.code === 'UNKNOWN') {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return "You're offline. Reconnect and try again.";
    }
    return "Couldn't reach the server. Check your connection and try again.";
  }

  const base = CODE_MAP[ctx.code] ?? ctx.message;

  if (ctx.code === 'VALIDATION_ERROR') {
    const fields = ctx.details?.['fields'];
    if (Array.isArray(fields) && fields.length > 0) {
      const bullets = fields
        .filter(isValidationFieldDetail)
        .slice(0, 3)
        .map((field) => {
          const path = Array.isArray(field.path)
            ? field.path.join('.')
            : typeof field.path === 'string'
              ? field.path
              : '';
          const message =
            typeof field.message === 'string' ? field.message : '';
          if (path && message) return `${path}: ${message}`;
          return message || path;
        })
        .filter((line) => line.length > 0);

      if (bullets.length > 0) {
        return `${base}\n• ${bullets.join('\n• ')}`;
      }
    }

    // No field details. `base` is the generic form copy from CODE_MAP, which
    // is right for a rejected form but wrong for the many VALIDATION_ERRORs
    // that aren't about a form at all — an export refusing because no WPS
    // establishment ID is configured, or because the month has no payable
    // records. Those arrive with a specific, actionable server message and
    // "Some fields need attention" actively misdirects the operator, who has
    // no form in front of them. Prefer the server's own wording whenever it
    // isn't the bare zod wrapper.
    if (ctx.message && ctx.message !== GENERIC_VALIDATION_MESSAGE) {
      return ctx.message;
    }
  }

  return base;
}
